import React, { useState, useEffect, useContext } from "react"
import { Link } from "react-router-dom"
import request from "request"

import { ApplicationContext } from "ApplicationContext"
import Header from "Header"
import AsyncContainer from "AsyncContainer"
import Button from "Button"

const SYNC_STATUS_COLORS = {
  success: "is-success",
  failed:  "is-danger",
  running: "is-warning",
  pending: "is-info",
}

const VivenuIntegration = () => {
  const { CurrentUserContext } = useContext(ApplicationContext)
  const { venue } = CurrentUserContext

  const [isLoaded, setIsLoaded]     = useState(false)
  const [integration, setInteg]     = useState(null)

  // form state
  const [apiKey, setApiKey]         = useState("")
  const [filter, setFilter]         = useState("")
  const [editingKey, setEditingKey] = useState(false)
  const [isSaving, setIsSaving]     = useState(false)
  const [isSyncing, setIsSyncing]   = useState(false)
  const [errors, setErrors]         = useState([])
  const [flashMsg, setFlashMsg]     = useState(null)

  const flash = (msg, type = "success") => {
    setFlashMsg({ msg, type })
    setTimeout(() => setFlashMsg(null), 4000)
  }

  const load = () =>
    request.get("/venue_admin/integrations").then(({ data }) => {
      const vi = (data.integrations || []).find(i => i.provider === "vivenu")
      setInteg(vi || null)
      setFilter(vi?.ticketTypeFilter || "")
      setIsLoaded(true)
    })

  useEffect(() => {
    let mounted = true
    load().catch(() => mounted && setIsLoaded(true))
    return () => { mounted = false }
  }, [venue])

  /* ── Save (create or update API key) ─────────────────────────── */
  const handleSave = () => {
    if (!apiKey.trim()) { setErrors(["API key is required"]); return }
    if (!filter.trim()) { setErrors(["Ticket type filter is required"]); return }
    setIsSaving(true); setErrors([])

    const method  = integration ? "patch" : "post"
    const url     = integration
      ? `/venue_admin/integrations/${integration.id}`
      : "/venue_admin/integrations"

    request[method](url, {
      integration: {
        provider: "vivenu",
        api_key: apiKey.trim(),
        ticket_type_filter: filter.trim(),
      },
    })
      .then(({ data }) => {
        setInteg(data.integration)
        setFilter(data.integration.ticketTypeFilter || "")
        setApiKey("")
        setEditingKey(false)
        setIsSaving(false)
        flash("Integration saved.")
      })
      .catch(err => {
        setErrors(err.response?.data?.errors || ["Failed to save"])
        setIsSaving(false)
      })
  }

  /* ── Save filter only ─────────────────────────────────────────── */
  const handleSaveFilter = () => {
    if (!filter.trim()) { flash("Ticket type filter cannot be blank.", "danger"); return }
    request.patch(`/venue_admin/integrations/${integration.id}`, {
      integration: { ticket_type_filter: filter.trim() },
    })
      .then(({ data }) => {
        setInteg(data.integration)
        flash("Filter updated.")
      })
      .catch(() => flash("Failed to update filter.", "danger"))
  }

  /* ── Sync ─────────────────────────────────────────────────────── */
  const handleSync = () => {
    setIsSyncing(true)
    request.post(`/venue_admin/integrations/${integration.id}/sync`)
      .then(() => {
        flash("Sync started — stats will refresh in a moment.")
        setTimeout(() => load().then(() => setIsSyncing(false)), 4000)
      })
      .catch(() => { setIsSyncing(false); flash("Sync failed to start.", "danger") })
  }

  /* ── Delete ───────────────────────────────────────────────────── */
  const handleDelete = () => {
    if (!confirm("Remove this integration? Existing imported credits will be kept.")) return
    request.delete(`/venue_admin/integrations/${integration.id}`)
      .then(() => { setInteg(null); setEditingKey(false); flash("Integration removed.") })
  }

  /* ── Render ───────────────────────────────────────────────────── */
  const syncTag = integration?.lastSyncStatus && (
    <span className={`tag ${SYNC_STATUS_COLORS[integration.lastSyncStatus]} ml-2`}>
      {integration.lastSyncStatus}
    </span>
  )

  return (
    <div>
      <AsyncContainer isLoaded={isLoaded}>

        {/* Flash */}
        {flashMsg && (
          <div className={`notification is-${flashMsg.type} is-light mb-4`}>
            {flashMsg.msg}
          </div>
        )}

        {/* Logo + title bar */}
        <div className="level mb-5">
          <div className="level-left">
            <div className="level-item">
              <img
                src="/images/integrations/vivenu_dark.png"
                alt="Vivenu"
                style={{ height: 36, marginRight: 16 }}
              />
            </div>
            <div className="level-item">
              <div>
                <p className="subtitle is-6 mb-0">Prepaid Credit import</p>
              </div>
            </div>
            {integration && (
              <div className="level-item">
                <span className={`tag ${integration.active ? "is-success" : "is-warning"}`}>
                  {integration.active ? "Active" : "Inactive"}
                </span>
                {syncTag}
              </div>
            )}
          </div>

          {integration && (
            <div className="level-right">
              <div className="level-item buttons mb-0">
                <Button
                  text={isSyncing ? "Syncing…" : "Sync Now"}
                  color="primary"
                  icon="sync"
                  onClick={handleSync}
                  disabled={isSyncing}
                />
                <Button
                  text="Edit Key"
                  icon="key"
                  onClick={() => { setEditingKey(true); setApiKey("") }}
                />
                <Button
                  text="Remove"
                  color="danger"
                  onClick={handleDelete}
                />
              </div>
            </div>
          )}
        </div>

        {/* Stats — only when configured */}
        {integration && (
          <div className="columns mb-5">
            {[
              { label: "Last Sync", value: integration.lastSyncedAt ? new Date(integration.lastSyncedAt).toLocaleString() : "Never" },
              { label: "Imported (last sync)", value: `${integration.lastSyncCount ?? 0} credits` },
              { label: "Ticket Filter (contains)", value: integration.ticketTypeFilter ? `"${integration.ticketTypeFilter}"` : "—" },
              { label: "API Key", value: integration.maskedApiKey, mono: true },
            ].map(({ label, value, mono }) => (
              <div className="column" key={label}>
                <div className="box has-text-centered" style={{ padding: "1rem" }}>
                  <p className="heading mb-1">{label}</p>
                  <p className={`is-size-6 has-text-weight-medium${mono ? " is-family-monospace" : ""}`}>
                    {value}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Setup / update-key form */}
        {(!integration || editingKey) && (
          <div className="box">
            <p className="title is-6 mb-4">{integration ? "Update API Key" : "Connect Vivenu"}</p>

            {!integration && (
              <div className="notification is-info is-light mb-4">
                <p>
                  Enter your Vivenu API key to start importing voucher tickets as Prepaid Credits.
                  Find it in your Vivenu dashboard under <strong>Settings → API</strong>.
                </p>
              </div>
            )}

            <div className="field">
              <label className="label">Vivenu API Key</label>
              <div className="control">
                <input
                  className="input"
                  type="password"
                  value={apiKey}
                  onChange={e => setApiKey(e.target.value)}
                  placeholder="key_…"
                  autoFocus
                />
              </div>
            </div>

            <div className="field">
              <label className="label">Ticket Type Filter</label>
              <div className="control">
                <input
                  className="input"
                  type="text"
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                  placeholder="e.g. Voucher"
                />
              </div>
              <p className="help">
                Imports any ticket whose name <strong>contains</strong> this text (case-insensitive).
                For example, <code>Voucher</code> matches <em>"$10 Food Voucher"</em> and <em>"Drink Voucher"</em>.
              </p>
            </div>

            {errors.length > 0 && (
              <div className="notification is-danger is-light">
                {errors.map((e, i) => <p key={i}>{e}</p>)}
              </div>
            )}

            <div className="buttons mt-3">
              <Button
                text={integration ? "Update" : "Connect"}
                color="primary"
                onClick={handleSave}
                disabled={isSaving}
              />
              {integration && (
                <Button text="Cancel" onClick={() => { setEditingKey(false); setErrors([]) }} />
              )}
            </div>
          </div>
        )}

        {/* Inline filter editor — visible when not editing the key */}
        {integration && !editingKey && (
          <div className="box">
            <p className="title is-6 mb-3">Ticket Type Filter</p>
            <p className="is-size-7 has-text-grey mb-3">
              Imports any ticket whose name <strong>contains</strong> this text (case-insensitive).
              For example, <code>Voucher</code> matches <em>"$10 Food Voucher"</em> and <em>"Drink Voucher"</em>.
            </p>
            <div className="field has-addons">
              <div className="control is-expanded">
                <input
                  className="input"
                  type="text"
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                  placeholder="e.g. Voucher"
                />
              </div>
              <div className="control">
                <Button
                  text="Save"
                  color="primary"
                  onClick={handleSaveFilter}
                  disabled={filter === (integration.ticketTypeFilter || "")}
                />
              </div>
            </div>
          </div>
        )}

        {/* How it works */}
        <div className="box mt-4">
          <p className="title is-6 mb-3">How it works</p>
          <div className="content is-small">
            <ol>
              <li>Enter your Vivenu API key and a ticket name filter (e.g. <code>Voucher</code>).</li>
              <li>Vivenu sends a webhook when a transaction completes, and tickets are also importable via <strong>Sync Now</strong>. Both paths use the same filter logic.</li>
              <li>A ticket is imported when its type name <strong>contains</strong> the filter text (case-insensitive). <code>Voucher</code> matches <em>"$10 Food Voucher"</em>, <em>"Drink Voucher"</em>, etc.</li>
              <li>Each matched ticket becomes a Prepaid Credit — the barcode is the credit code and the ticket price is the balance.</li>
              <li>Re-syncing is safe: already-imported tickets are skipped.</li>
            </ol>
          </div>
        </div>

      </AsyncContainer>
    </div>
  )
}

export default VivenuIntegration
